import { 
    Box, Typography, Button, Paper, ToggleButtonGroup, ToggleButton, 
    FormGroup, FormControlLabel, Checkbox 
} from '@mui/material';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { PlannerFormModel } from '../formik/planner/PlannerFormModel';
import moment from 'moment';
import AutocompleteField from 'components/formFields/autocompleteField';
import InputField from 'components/formFields/InputField';
import AutocompleteMultiple from 'components/formFields/AutocompleteMultiple copy';
 
const { formField: { cargo, turnos, cantidad, repeticion, diasSemana } } = PlannerFormModel;

const diasSemanaOptions = [
    { value: '0', label: 'D' },
    { value: '1', label: 'L' },
    { value: '2', label: 'M' },
    { value: '3', label: 'X' },
    { value: '4', label: 'J' },
    { value: '5', label: 'V' },
    { value: '6', label: 'S' }
];

interface Props {
    onApply: (values: any) => void;
    selectedDates: string[];
}

export const PlannerFormSidebar = ({ onApply, selectedDates }: Props) => {
    const { values, setFieldValue, isValid, dirty } = useFormikContext<any>();
    const { cargos_select } = useSelector((state: any) => state.cargos);
    const { templates_for_select } = useSelector((state: any) => state.shiftTemplates || { templates_for_select: [] });

    const handleRepeticionChange = (event: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
        if (newAlignment !== null) {
            setFieldValue(repeticion.name, newAlignment);
        }
    };

    const handleDiaToggle = (diaVal: string) => {
        const curr = values[diasSemana.name] as string[];
        if (curr.includes(diaVal)) {
            setFieldValue(diasSemana.name, curr.filter(d => d !== diaVal));
        } else {
            setFieldValue(diasSemana.name, [...curr, diaVal]);
        }
    };

    return (
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #E0E3E7', borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Asignar Demanda</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Días seleccionados: <strong>{selectedDates.length}</strong>
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 }}>
                <AutocompleteField 
                    name={cargo.name} 
                    label={cargo.label} 
                    options={cargos_select || []} 
                    fullWidth 
                />

                <AutocompleteMultiple
                    name={turnos.name}
                    label={turnos.label}
                    options={templates_for_select || []}
                    fullWidth
                />

                <InputField 
                    name={cantidad.name} 
                    label={cantidad.label} 
                    type="number" 
                    fullWidth 
                />

                <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Repetición</Typography>
                    <ToggleButtonGroup
                        value={values[repeticion.name]}
                        exclusive
                        onChange={handleRepeticionChange}
                        fullWidth
                        orientation="vertical"
                        size="small"
                        color="primary"
                    >
                        <ToggleButton value="selected">Solo días seleccionados</ToggleButton>
                        <ToggleButton value="range">Todos los días del rango</ToggleButton>
                        <ToggleButton value="weekdays">Lunes a Viernes</ToggleButton>
                        <ToggleButton value="weekends">Fines de semana</ToggleButton>
                        <ToggleButton value="custom">Personalizado</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {values[repeticion.name] === 'custom' && (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Días de la semana</Typography>
                        <FormGroup row sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            {diasSemanaOptions.map(dia => (
                                <FormControlLabel
                                    key={dia.value}
                                    control={
                                        <Checkbox 
                                            size="small" 
                                            checked={values[diasSemana.name].includes(dia.value)}
                                            onChange={() => handleDiaToggle(dia.value)}
                                        />
                                    }
                                    label={<Typography variant="caption">{dia.label}</Typography>}
                                    labelPlacement="bottom"
                                    sx={{ m: 0 }}
                                />
                            ))}
                        </FormGroup>
                    </Box>
                )}
            </Box>

            <Button 
                variant="contained" 
                color="primary" 
                fullWidth 
                disabled={!isValid || !dirty}
                onClick={() => onApply(values)}
                sx={{ mt: 3, py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
            >
                Aplicar
            </Button>

            {values.cargo && values.cantidad && (
                <Box sx={{ 
                    mt: 3, 
                    p: 2, 
                    bgcolor: 'rgba(105, 108, 255, 0.04)', 
                    borderRadius: 2, 
                    border: '1px solid rgba(105, 108, 255, 0.2)' 
                }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                        Resumen de Programación
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2">
                            <strong>Fechas:</strong> {moment(selectedDates[0]).format('DD/MM/YYYY')} 
                            {selectedDates.length > 1 ? ` al ${moment(selectedDates[selectedDates.length - 1]).format('DD/MM/YYYY')}` : ''}
                        </Typography>
                        
                        <Typography variant="body2">
                            <strong>Cargo:</strong> {values.cargo?.label || 'No seleccionado'}
                        </Typography>
                        
                        {values.turnos && values.turnos.length > 0 && (
                            <Typography variant="body2">
                                <strong>Turnos:</strong> {values.turnos.map((t: any) => t.label).join(', ')}
                            </Typography>
                        )}
                        
                        <Typography variant="body2">
                            <strong>Cantidad:</strong> {values.cantidad} persona(s)
                        </Typography>
                    </Box>
                </Box>
            )}
        </Paper>
    );
};
